import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Form, Input, Button, Select } from "antd";
import { Spin, notification, Row, Col, message, Upload } from "antd";
import {
  LoadingOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Navigate, useNavigate } from "react-router-dom";
import { generateFileUrl, getFileImage, updateProfile } from "../../utils/api";

import "./Profile.css";

const antIcon = (
  <LoadingOutlined style={{ fontSize: 24, color: "white" }} spin />
);
const { Option } = Select;

const getBase64 = (img, callback) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
};
const beforeUpload = (file) => {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  const isLt2M = file.size / 1024 / 1024 < 5;
  if (!isLt2M) {
    message.error("Image must smaller than 5MB!");
  }
  return isJpgOrPng && isLt2M;
};

function Profile({ navStore, authStore }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [organization, setOrganization] = useState("");
  const [organizationList, setOrganizationList] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState();
  const [age, setAge] = useState();
  const [weight, setWeight] = useState();
  const [heightFt, setHeightFt] = useState();
  const [heightIn, setHeightIn] = useState();
  const [photo, setPhoto] = useState();
  const [staticImageUrl, setStaticImageUrl] = useState();

  const [imageBinaryUrl, setImageBinaryUrl] = useState();

  const [fileUrl, setFileUrl] = useState();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const onFinishWithMessage = async (message) => {
    setError("");
    setSubmitting(true);
    try {
      await updateProfile(
        authStore.user.id,
        photo,
        firstName,
        lastName,
        phone,
        age,
        weight,
        heightFt,
        heightIn
      );
      notification.open({
        message: message,
      });
    } catch (e) {
      setSubmitting(false);
      console.log(e);
      setError(e.response.data.message);
    } finally {
      setSubmitting(false);
    }
  };
  const onFinish = async () => {
    onFinishWithMessage("Profile Updated");
  };

  useEffect(() => {
    async function generate() {
      const fileURL = await generateFileUrl();
      setFileUrl(fileURL);
    }
    generate();
  }, []);

  useEffect(() => {
    const user = authStore.user;
    if (!user) {
      return;
    }
    const fieldValues = {};
    if (user.profile) {
      setPhone(user.profile.phone);
      fieldValues["phone"] = user.profile.phone;

      setAge(user.profile.age);
      fieldValues["age"] = user.profile.age;

      setWeight(user.profile.weight);
      fieldValues["weight"] = user.profile.weight;

      setHeightFt(user.profile.height_ft);
      fieldValues["heightFt"] = user.profile.height_ft;

      setHeightIn(user.profile.height_in);
      fieldValues["heightIn"] = user.profile.height_in;
    }
    setFirstName(user.firstname);
    fieldValues["firstName"] = user.firstname;

    setLastName(user.lastname);
    fieldValues["lastName"] = user.lastname;

    form.setFieldsValue(fieldValues);
  }, [authStore.user]);

  async function fetchProfile(key) {
    const image = await getFileImage(key);
    setStaticImageUrl(image.url);
  }

  useEffect(() => {
    const user = authStore.user;
    if (!user) {
      return;
    }
    if (user.profile && user.profile.photo) {
      fetchProfile(user.profile.photo);
    }
  }, [authStore.user]);

  const removePhoto = async () => {
    setStaticImageUrl(null);
    setImageBinaryUrl(null);
    setPhoto(null);
    onFinishWithMessage("Photo removed");
  };

  const handleChange = (info) => {
    if (info.file.status === "uploading") {
      setLoading(true);
      return;
    }
    if (info.file.status === "done") {
      // Get this url from response in real world.
      getBase64(info.file.originFileObj, (url) => {
        setLoading(false);
        setImageBinaryUrl(url);
        setPhoto(fileUrl.file.fields.key);
        fetchProfile(fileUrl.file.fields.key);
        onFinishWithMessage("Photo uploaded");
      });
    }
  };
  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </div>
  );

  return (
    <div className="Profile">
      <div className="Profile__wrapper">
        <div className="Profile__container">
          <h2>Profile</h2>
          {staticImageUrl ? (
            <div>
              <div
                className="Profile__image"
                style={{ backgroundImage: `url(${staticImageUrl})` }}
              ></div>
              <Button
                type="text"
                size="small"
                className="Profile__image_remove"
                onClick={() => {
                  removePhoto();
                }}
              >
                <DeleteOutlined />
              </Button>
              <div className="clear"></div>
            </div>
          ) : (
            <div>
              <Upload
                name="file"
                listType="picture-card"
                className="avatar-uploader"
                data={fileUrl?.file?.fields}
                showUploadList={false}
                action={fileUrl?.file?.url}
                beforeUpload={beforeUpload}
                onChange={handleChange}
              >
                {uploadButton}
              </Upload>
            </div>
          )}

          <Form
            name="basic"
            initialValues={{ remember: true }}
            autoComplete="off"
            layout="vertical"
            form={form}
            onFinish={onFinish}
          >
            <Row>
              <Col span={12}>
                <Form.Item
                  className="Profile__item"
                  label="First name"
                  name="firstName"
                  rules={[
                    { required: true, message: "Please enter first name" },
                  ]}
                >
                  <Input onChange={(e) => setFirstName(e.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  className="Profile__item"
                  label="Last name"
                  name="lastName"
                  rules={[
                    { required: true, message: "Please enter last name" },
                  ]}
                >
                  <Input onChange={(e) => setLastName(e.target.value)} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <Form.Item
                  className="Profile__item"
                  label="Phone"
                  name="phone"
                  rules={[
                    { required: false, message: "Please your phone number" },
                  ]}
                >
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <Form.Item
                  className="Profile__item"
                  label="Age"
                  name="age"
                  rules={[
                    { required: false, message: "Please enter your age" },
                  ]}
                >
                  <Input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="Profile__item"
                  label="Weight (lbs)"
                  name="weight"
                  rules={[
                    { required: false, message: "Please enter your weight" },
                  ]}
                >
                  <Input
                    value={weight}
                    type="number"
                    onChange={(e) => setWeight(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="Profile__item"
                  label="Height"
                  name="height"
                  rules={[
                    { required: false, message: "Please enter your height" },
                  ]}
                >
                  <Row>
                    <Col>
                      <Input
                        value={heightFt}
                        className="Profile__input_small"
                        type="number"
                        onChange={(e) => setHeightFt(e.target.value)}
                      />{" "}
                      ft.
                    </Col>
                    <Col>
                      <Input
                        value={heightIn}
                        className="Profile__input_small"
                        type="number"
                        onChange={(e) => setHeightIn(e.target.value)}
                      />{" "}
                      in.
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>

            {error && <div className="error">{error}</div>}
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                {submitting && <Spin indicator={antIcon} />}
                {!submitting && <span> Save</span>}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default observer(Profile);
